import axios from "axios";
import { refreshApi, logoutApi } from "./AuthService";
import { urlBase } from "../config";
import { useAuthStore } from "../store/authStore";
import { SESSION } from "../config/sessionConfig";

const api = axios.create({
  baseURL: urlBase,
});

// Request interceptor – dodaje access token
api.interceptors.request.use(config => {
  const token = useAuthStore.getState().getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, error => {
  return Promise.reject(error);
});

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

async function logout() {
  const accessToken = useAuthStore.getState().getToken();
  const refreshToken = useAuthStore.getState().getRefreshToken();
  await logoutApi(accessToken, refreshToken).catch(() => {});

  useAuthStore.getState().logout();
  sessionStorage.clear();
  localStorage.removeItem('pelikan-auth');
  localStorage.removeItem('pelikan-last-activity');
  localStorage.removeItem('pelikan-refresh-lock');

  window.location.href = "/SignIn";
}

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response?.status === 429) {
      console.warn("Rate limit przekroczony. Spróbuj ponownie za chwilę.");
      return Promise.reject(error);
    }

    if (error.response?.status === 403 && error.response?.data?.error === "MFA_SETUP_REQUIRED") {
      useAuthStore.getState().setMfaStatus({
        twoFactorEnabled: false,
        mfaSetupEnforced: true,
        mfaDeadline: null,
        daysRemaining: 0,
      });
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Weryfikacja 2h bezczynności przed odświeżeniem tokena
        try {
          const storedLastActivity = parseInt(localStorage.getItem('pelikan-last-activity') || '0', 10);
          if (storedLastActivity > 0 && Date.now() - storedLastActivity >= SESSION.IDLE_THRESHOLD_MS) {
            await logout();
            return Promise.reject(error);
          }
        } catch (e) {}

        const refreshToken = useAuthStore.getState().getRefreshToken();
        if (!refreshToken) {
          await logout();
          return Promise.reject(error);
        }

        const refreshResult = await refreshApi(refreshToken);

        if (!refreshResult.isSuccess) {
          try {
            useAuthStore.persist?.rehydrate?.();
          } catch (e) {}
          const latestRefreshToken = useAuthStore.getState().getRefreshToken();
          const latestAccessToken = useAuthStore.getState().getToken();
          if (latestRefreshToken && latestRefreshToken !== refreshToken && latestAccessToken) {
            originalRequest.headers.Authorization = `Bearer ${latestAccessToken}`;
            onRefreshed(latestAccessToken);
            return api(originalRequest);
          }
          if (!refreshResult.isRateLimited) {
            await logout();
          }
          return Promise.reject(error);
        }

        useAuthStore.getState().updateAccessToken(refreshResult.newAuthToken, refreshResult.newRefreshToken);
        originalRequest.headers.Authorization = `Bearer ${refreshResult.newAuthToken}`;

        onRefreshed(refreshResult.newAuthToken);

        return api(originalRequest);

      } catch (err) {
        await logout();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    console.error(`Błąd API [${error.config?.method?.toUpperCase()}] ${error.config?.url}:`, error.response?.status, error.message);

    return Promise.reject(error);
  }
);

export default api;
